import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import BannerTableData from "Views/Banner/data/BannerTableData";
import { useEffect, useMemo, useState } from "react";
import ArgonButton from "components/ArgonButton";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import Dropdown from "components/CustomDropdown/Dropdown";
import { toast } from "react-toastify";
import api from "utils/api";
import { Link } from "react-router-dom";
import { audienceLabel, BANNER_AUDIENCES, BANNER_STATUSES } from "Views/Banner/bannerOptions";

function Tables() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBannerId, setSelectedBannerId] = useState(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  const filteredBanners = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return banners.filter((banner) => {
      const matchesQuery =
        !query ||
        banner.title?.toLowerCase().includes(query) ||
        banner.bannerType?.toLowerCase().includes(query) ||
        banner.status?.toLowerCase().includes(query) ||
        audienceLabel(banner.audience, banner.onlyActiveClass).toLowerCase().includes(query);
      const matchesStatus = !statusFilter || banner.status === statusFilter;
      const matchesAudience = !audienceFilter || (banner.audience || "ALL") === audienceFilter;
      return matchesQuery && matchesStatus && matchesAudience;
    });
  }, [banners, searchQuery, statusFilter, audienceFilter]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await api.get("/banners");
      if (response.data.success) {
        setBanners(response.data.banners || []);
      } else {
        toast.error("Failed to fetch banners");
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      toast.error(error.response?.data?.message || "Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedBannerId(id);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedBannerId(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await api.delete(`/banners/${selectedBannerId}`);
      if (response.data.success) {
        toast.success(response.data.message || "Banner deleted successfully");
        fetchBanners();
      } else {
        toast.error(response.data.message || "Failed to delete banner");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      toast.error(error.response?.data?.message || "Failed to delete banner");
    } finally {
      handleDeleteClose();
    }
  };

  const { columns, rows } = BannerTableData(filteredBanners, handleDeleteClick);
  const liveCount = banners.filter((banner) => banner.status === "ACTIVE").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <ArgonBox flexShrink={0}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  Banner List
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text">
                  {filteredBanners.length} shown · {liveCount} active on the apps
                </ArgonTypography>
              </ArgonBox>
              <ArgonBox display="flex" gap={1.5} alignItems="center" flexWrap="nowrap" flexShrink={0}>
                <ArgonInput
                  placeholder="Search banners..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  sx={{ width: 220, flex: "0 0 220px" }}
                />
                <Dropdown
                  width={160}
                  value={statusFilter}
                  options={[{ value: "", label: "All statuses" }, ...BANNER_STATUSES]}
                  onChange={(event) => setStatusFilter(event.target.value)}
                />
                <Dropdown
                  width={170}
                  value={audienceFilter}
                  options={[{ value: "", label: "All audiences" }, ...BANNER_AUDIENCES]}
                  onChange={(event) => setAudienceFilter(event.target.value)}
                />
                <Link to="/banner/addbanner" style={{ textDecoration: "none", flexShrink: 0 }}>
                  <ArgonButton variant="contained" color="primary" sx={{ borderRadius: 2, width: 130, whiteSpace: "nowrap" }}>
                    Add Banner
                  </ArgonButton>
                </Link>
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress />
                </ArgonBox>
              ) : filteredBanners.length === 0 ? (
                <ArgonBox p={5} textAlign="center">
                  <ArgonTypography variant="h6" color="text">
                    {searchQuery || statusFilter || audienceFilter
                      ? "No banners found matching your filters"
                      : "No banners available"}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>

        <DeleteDialog
          open={deleteOpen}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          title="Are you sure you want to delete this Banner?"
          description="This action cannot be undone."
          cancelText="Cancel"
          confirmText="Delete"
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
